import { useState, useEffect, useMemo } from 'react'
import { useReactFlow } from '@xyflow/react'
import { AICommandType, AICommandStatus } from '@/utils/aiCommandUtils'
import type { SmartNotesNodeData } from '../types'

interface UseSmartNotesProcessingProps {
  id: string
  data: SmartNotesNodeData
  syncNodeData: (updates: Partial<SmartNotesNodeData>) => void
}

export const useSmartNotesProcessing = ({
  id,
  data,
  syncNodeData,
}: UseSmartNotesProcessingProps) => {
  const { addNodes, getNode } = useReactFlow()
  const [createCooldowns, setCreateCooldowns] = useState<Record<number, boolean>>({})
  const [pendingDesignerNodeId, setPendingDesignerNodeId] = useState<string | null>(null)
  const [pendingDesignerDocType, setPendingDesignerDocType] = useState<string | null>(null)

  // Parse URLs from content
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const urls = useMemo(() => {
    if (!data.content) return []
    const allUrls = Array.from(data.content.matchAll(urlRegex), (m) => m[0])
    return Array.from(new Set(allUrls))
  }, [data.content])

  // Handle analyze with link extraction
  const handleAnalyze = async () => {
    if (!data.content?.trim()) {
      const errorMsg = 'Error: Please enter some text to analyze.'
      syncNodeData({
        statusMessage: errorMsg,
      })
      return
    }

    const processingMsg = 'Analyzing content...'
    syncNodeData({
      statusMessage: processingMsg,
      documentRequest: {
        ...(data.documentRequest || {}),
        document: 'smart-notes-analysis',
        'sysagent-cmd': 'SmartNotesNode-Analyze-Started',
        'sysagent-response': '',
        processing: true,
        timestamp: Date.now(),
        [id]: { content: data.content },
      },
    })

    // If extractLinks is checked and there are URLs, create NodeWebExtract node
    if (data.extractLinks && urls.length > 0) {
      await handleExtractLinks()
      return
    }

    // If not extracting, start analysis immediately
    const documentRequest = {
      document: 'smart-notes-analysis',
      'sysagent-cmd': 'SmartNotesNode-Analyze-Started',
      'sysagent-response': '',
      processing: true,
      timestamp: Date.now(),
      [id]: { content: data.content },
    }
    syncNodeData({ documentRequest })
  }

  // Handle link extraction
  const handleExtractLinks = async () => {
    const newExtractNodeId = `webextract-${Date.now()}`
    const offset = { x: 1200, y: 0 }
    const currentNode = (window as any).__flowGetAllNodes?.().find((n: any) => n.id === id)
    const pos = currentNode?.position || { x: 0, y: 0 }
    const newPos = { x: pos.x + offset.x, y: pos.y + offset.y }

    const urlsString = urls.join(',')

    const newWebExtractNode = {
      id: newExtractNodeId,
      type: 'nodewebextract',
      position: newPos,
      data: {
        createdBy: data.createdBy,
        url: urlsString,
        informationClassification: 'Data',
        mode: 'normal',
        includeImages: false,
        label: 'Web Extract',
        statusMessage: '',
        extractedContent: '',
        images: [],
        adultContent: data.extractAdultContent,
        depthLevel: data.extractAdultContent ? data.extractDepthLevel : undefined,
        webExtractCommand: {
          'sysagent-cmd': 'WebExtract-Process-Started',
          processing: true,
          timestamp: Date.now(),
        },
      },
      width: 900,
      height: 900,
      draggable: true,
      selectable: true,
      connectable: true,
    }

    addNodes([newWebExtractNode])

    // Uncheck extractLinks after extraction is triggered
    syncNodeData({ extractLinks: false })

    // Wait for extraction to finish and then start analysis
    const extractedContent = await pollExtractedContent(newExtractNodeId)
    if (extractedContent) {
      // Add as new reference
      const newReferences = { ...(data.setReferences || {}) }
      newReferences[newExtractNodeId] = ['fullDocument']
      syncNodeData({
        setReferences: newReferences,
        documentRequest: {
          document: 'smart-notes-analysis',
          'sysagent-cmd': 'SmartNotesNode-Analyze-Started',
          'sysagent-response': '',
          processing: true,
          timestamp: Date.now(),
          [id]: { content: data.content },
        }
      })
    } else {
      syncNodeData({ statusMessage: 'Extraction failed or timed out.' })
    }
  }

  // Poll for extracted content
  const pollExtractedContent = async (nodeId: string): Promise<string> => {
    let tries = 0
    let extractedContent = ''
    while (tries < 60) {
      const allNodes = (window as any).__flowGetAllNodes?.() || []
      const extractNode = allNodes.find((n: any) => n.id === nodeId)
      extractedContent = extractNode?.data?.extractedContent || ''
      if (extractedContent && extractedContent.length > 10) break
      await new Promise((res) => setTimeout(res, 1000))
      tries++
    }
    return extractedContent
  }

  // Handle creating a new document based on recommendation
  const handleCreateDocument = async (recommendation: any, index?: number) => {
    // Prevent spamming with cooldown
    if (typeof index === 'number') {
      setCreateCooldowns((prev) => ({ ...prev, [index]: true }))
      setTimeout(() => {
        setCreateCooldowns((prev) => ({ ...prev, [index]: false }))
      }, 2000)
    }

    let docType = ''
    let purpose = ''
    let documentKey = ''
    let action = ''
    let description = ''

    if (typeof recommendation === 'string') {
      if (!recommendation.includes('Create a new')) return
      const match = /Create a new "([^"]+)" with purpose: ([^.]+\.)/.exec(recommendation)
      if (!match) return
      docType = match[1]
      purpose = match[2].replace(/\.$/, '')
      action = 'create-new'
    } else if (typeof recommendation === 'object' && recommendation !== null) {
      action = recommendation.action
      docType = recommendation.title || ''
      purpose = recommendation.purpose || ''
      documentKey = recommendation.documentKey || ''
      description = recommendation.description || ''
      if (!docType) return
    } else {
      return
    }

    if (action === 'use-existing') {
      await handleUseExistingTemplate(docType, documentKey)
    } else {
      await handleCreateNewDocument(docType, purpose, description, data.createdBy)
    }
  }

  // Handle using existing template
  const handleUseExistingTemplate = async (docType: string, documentKey: string) => {
    // This would need access to templateList - could be passed as prop or fetched
    const templateContent = 'Document template content...' // Placeholder

    let context = data.content || ''
    if (data.context) {
      context += `\n\n--- SmartNotesNode Context ---\n${data.context}`
    }
    if (data.linkedKnowledge) {
      context += `\n\n--- SmartNotesNode LinkedKnowledge ---\n${data.linkedKnowledge}`
    }

    const currentNode = getNode(id)
    const pos = currentNode?.position || { x: 0, y: 0 }
    const offset = { x: 850, y: 0 }
    const newPos = { x: pos.x + offset.x, y: pos.y + offset.y }
    const newDocId = `document-node-${Date.now()}`

    const newDocumentNode = {
      id: newDocId,
      type: 'nodedocument',
      position: newPos,
      width: 1200,
      height: 1600,
      data: {
        createdBy: data.createdBy,
        label: docType,
        informationClassification: 'Synthesis',
        document: documentKey,
        contentMarkdown: templateContent,
        context: context,
        statusMessage: 'Processing document template...',
        documentCommand: {
          'sysagent-cmd': 'DocumentNode-Process-Start',
          mode: 'smart-format',
          processing: true,
        },
      },
      draggable: true,
      selectable: true,
      connectable: true,
    }

    addNodes([newDocumentNode])
    syncNodeData({ linkedNodes: [...(data.linkedNodes || []), newDocId] })
  }

  // Handle creating new document via designer
  const handleCreateNewDocument = async (docType: string, purpose: string, description: string, email: string) => {
    const designParameters = {
      documentStyle: docType.toLowerCase().includes('technical') ? 'technical' : 'business',
      customStyle: '',
      audience: docType.includes('Pitch Deck') ? 'investors' : 'technical_team',
      purpose: purpose,
      technicalLevel: docType.includes('Technical') ? 'advanced' : 'intermediate',
      structure: 'standard',
    }

    const currentNode = getNode(id)
    const pos = currentNode?.position || { x: 0, y: 0 }
    const offset = { x: 850, y: 0 }
    const newPos = { x: pos.x + offset.x, y: pos.y + offset.y }
    const newDocId = `document-designer-${Date.now()}`

    const documentRequest = {
      type: AICommandType.DOCUMENT_DESIGNER,
      status: AICommandStatus.STARTED,
      payload: {
        [newDocId]: {
          content: purpose + '\n\n' + description,
          designParameters: designParameters,
          document: 'document-designer',
          processing: true,
          timestamp: Date.now(),
        },
      },
      'sysagent-cmd': 'NodeDocumentDesigner-Generate-Started',
      processing: true,
      processingStartedAt: Date.now(),
      message: 'Generating document...',
    }

    const newDocumentNode = {
      id: newDocId,
      type: 'nodedocumentdesigner',
      position: newPos,
      data: {
        createdBy: data.createdBy,
        label: docType,
        informationClassification: 'Synthesis',
        content: purpose + '\n\n' + description,
        designParameters,
        documentRequest,
        statusMessage: 'Generating document...',
      },
      width: 900,
      height: 822,
      draggable: true,
      selectable: true,
      connectable: true,
    }

    addNodes([newDocumentNode])
    syncNodeData({ linkedNodes: [...(data.linkedNodes || []), newDocId] })

    // Track this designer node for post-generation handling
    setPendingDesignerNodeId(newDocId)
    setPendingDesignerDocType(docType)
  }

  // Effect to handle document request processing
  useEffect(() => {
    const dr = data.documentRequest
    if (
      dr &&
      dr.processing === false &&
      dr['sysagent-cmd'] === '' &&
      dr['sysagent-response'] === 'SmartNotesNode-Analysis-Complete' &&
      Array.isArray(dr.recommendations) &&
      dr.recommendations.length > 0
    ) {
      console.log('SmartNotes: Transferring recommendations from documentRequest to data.recommendations:', dr.recommendations);
      syncNodeData({
        recommendations: dr.recommendations,
        statusMessage: 'Analysis complete.',
        documentRequest: undefined,
      })
    } else if (
      dr &&
      dr.processing === false &&
      dr['sysagent-response'] === 'SmartNotesNode-Analysis-Error'
    ) {
      console.log('SmartNotes: Analysis error detected');
      syncNodeData({
        statusMessage: 'Error during analysis.',
        documentRequest: undefined,
      })
    }
  }, [data.documentRequest, syncNodeData])

  // Effect to handle pending designer node completion
  useEffect(() => {
    if (!pendingDesignerNodeId) return

    let cancelled = false
    const pollDesignerNode = async () => {
      let tries = 0
      while (tries < 60 && !cancelled) {
        const allNodes = (window as any).__flowGetAllNodes?.() || []
        const designerNode = allNodes.find((n: any) => n.id === pendingDesignerNodeId)
        const generatedMarkdown = designerNode?.data?.generatedMarkdown

        if (
          generatedMarkdown &&
          (!designerNode.data.documentRequest?.processing ||
            designerNode.data.documentRequest?.processing === false)
        ) {
          // Extract markdown content and create document node
          const markdownContent = extractMarkdownSection(generatedMarkdown)

          let context = data.content || ''
          if (data.context) {
            context += `\n\n--- SmartNotesNode Context ---\n${data.context}`
          }
          if (data.linkedKnowledge) {
            context += `\n\n--- SmartNotesNode LinkedKnowledge ---\n${data.linkedKnowledge}`
          }

          const currentNode = getNode(id)
          const pos = currentNode?.position || { x: 0, y: 0 }
          const offset = { x: 1000, y: 0 }
          const newPos = { x: pos.x + offset.x, y: pos.y + offset.y }
          const newDocId = `document-node-${Date.now()}`

          const newDocumentNode = {
            id: newDocId,
            type: 'nodedocument',
            position: newPos,
            width: 1200,
            height: 1600,
            data: {
              createdBy: data.createdBy,
              label: pendingDesignerDocType || 'Generated Document',
              informationClassification: 'Synthesis',
              document: '',
              contentMarkdown: markdownContent,
              context: context,
              statusMessage: 'Processing generated document...',
              documentCommand: {
                'sysagent-cmd': 'DocumentNode-Process-Start',
                mode: 'smart-format',
                processing: true,
              },
            },
            draggable: true,
            selectable: true,
            connectable: true,
          }

          addNodes([newDocumentNode])
          syncNodeData({ linkedNodes: [...(data.linkedNodes || []), newDocId] })
          setPendingDesignerNodeId(null)
          setPendingDesignerDocType(null)
          break
        }
        await new Promise((res) => setTimeout(res, 1000))
        tries++
      }
    }

    pollDesignerNode()
    return () => {
      cancelled = true
    }
  }, [pendingDesignerNodeId, data.content, data.context, data.linkedKnowledge, data.linkedNodes, addNodes, getNode, id, syncNodeData, pendingDesignerDocType])

  // Helper function to extract markdown section
  const extractMarkdownSection = (content: string): string => {
    // This is a simplified version - the actual implementation would be more sophisticated
    return content.replace(/```markdown\n?|```\n?/g, '').trim()
  }

  return {
    handleAnalyze,
    handleCreateDocument,
    createCooldowns,
    urls,
  }
}
